import { useCallback, useEffect, useState } from "react";
import { SITE_THEME_KEY } from "@/lib/storage";

type SiteTheme = "dark" | "light";

function readInitialTheme(): SiteTheme {
  try {
    return window.localStorage.getItem(SITE_THEME_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function useSiteTheme() {
  const [theme, setTheme] = useState<SiteTheme>("dark");

  useEffect(() => {
    const initial = readInitialTheme();
    setTheme(initial);
    applyTheme(initial);
    return () => {
      // Reset on unmount (e.g. navigating to /console).
      document.body.removeAttribute("data-site-theme");
    };
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: SiteTheme = prev === "light" ? "dark" : "light";
      applyTheme(next);
      try {
        window.localStorage.setItem(SITE_THEME_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}

function applyTheme(theme: SiteTheme) {
  if (theme === "light") {
    document.body.setAttribute("data-site-theme", "light");
  } else {
    document.body.removeAttribute("data-site-theme");
  }
}
